import { Button, Form } from 'components';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { media } from 'utils/rwd';
import { useUpdateLocation } from 'api/locations/hooks';
import validationSchemas from './schemas';

const LocationInfoForm = ({ type = 'update', initialValues, onNext, onPrevious, prependUrl }) => {
  const { t } = useTranslation('ownerPortal', { keyPrefix: 'locations' });
  const { mutate: updateLocation, isPending } = useUpdateLocation();
  const formInitialValues = useMemo(
    () => ({
      uid: initialValues?.uid || undefined,
      name: initialValues.name || '',
      description: initialValues.description || '',
      region: initialValues.region || '',
      vanityUrl: initialValues.vanityUrl || '',
    }),
    [initialValues],
  );
  const validationSchema = useMemo(() => validationSchemas.location_info, []);

  const handleSubmit = useCallback(
    ({ uid, name, description, vanityUrl }) => {
      if (type === 'update') {
        updateLocation(
          { uid, content: { name, description, vanity_url: vanityUrl } },
          {
            onSuccess: () => {
              if (onNext) onNext();
            },
          },
        );
      } else if (onNext) {
        onNext();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updateLocation, onNext, type],
  );

  return (
    <Form
      initialValues={formInitialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      renderDefaultButtons={false}
    >
      <Form.GroupVertical label={t('onboarding.nameLabel')} required>
        <Form.Input name="name" />
      </Form.GroupVertical>

      <Form.GroupVertical label={t('onboarding.descriptionLabel')}>
        <Form.Input name="description" />
      </Form.GroupVertical>

      <Form.GroupVertical label={t('onboarding.regionLabel')}>
        <Form.Input name="region" readOnly />
      </Form.GroupVertical>

      <Form.GroupVertical label={t('onboarding.vanityUrlLabel')} required>
        <Form.Input name="vanityUrl" prepend={<PrependText>{prependUrl}</PrependText>} />
      </Form.GroupVertical>

      <ButtonsSection>
        {onPrevious && (
          <Button variant="secondary" block onClick={onPrevious}>
            {t('onboarding.previousStep')}
          </Button>
        )}
        <Form.Submit
          className="text-uppercase mt-0"
          size="lg"
          variant="primary"
          block
          isLoading={isPending}
          ignoreClickedState
        >
          {t('onboarding.nextStep')}
        </Form.Submit>
      </ButtonsSection>
    </Form>
  );
};

const PrependText = styled.span`
  display: block;
  max-width: 140px;
  min-width: 0;
  flex: 0 1 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${media.sm`
    max-width: 200px;
  `}

  ${media.md`
    max-width: 100px;
  `}
`;

const ButtonsSection = styled.div`
  width: 100%;
  margin-top: 48px;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
`;

LocationInfoForm.propTypes = {
  type: PropTypes.oneOf(['update']),
  initialValues: PropTypes.shape({
    uid: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    vanityUrl: PropTypes.string,
  }),
  onNext: PropTypes.func,
  onPrevious: PropTypes.func,
};

export default LocationInfoForm;
